/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { VNode, h } from "preact";
import qrcode from "qrcode-generator";

export interface TalerQrCodeLabels {
  copy: string;
  copied: string;
  generationFailed: string;
}

export interface TalerQrCodeProps {
  url: string;
  copyUrl?: string;
  size?: number;
  alt?: string;
  variant?: "taler" | "swiss" | "plain";
  labels?: Partial<TalerQrCodeLabels>;
}

const DEFAULT_LABELS: TalerQrCodeLabels = {
  copy: "Copy URI",
  copied: "✓ Copied!",
  generationFailed: "The QR code could not be generated.",
};

// Short adjacent strokes make one soft gradient line. The dash pattern repeats
// halfway around the frame, producing the two opposing lines in the brand
// design while still letting both bend cleanly around rounded corners.
const TALER_QR_LINE_COLORS = Array.from({ length: 41 }, (_, index) => {
  const progress = index / 40;
  const brandStrength = 1 - Math.abs(progress * 2 - 1);
  const neutral = [241, 241, 244];
  const brand = [0, 66, 179];
  const channel = (i: number): number =>
    Math.round(neutral[i]! + (brand[i]! - neutral[i]!) * brandStrength);
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
});

export function generateQrDataUrl(
  text: string,
): { type: "success"; url: string } | { type: "error" } {
  try {
    const qr = qrcode(0, "H");
    qr.addData(text, "Byte");
    qr.make();
    const svgTag = qr.createSvgTag({
      scalable: true,
      margin: 1,
    });
    return {
      type: "success",
      url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgTag)}`,
    };
  } catch {
    return { type: "error" };
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    if (typeof document === "undefined") return false;
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    try {
      textarea.select();
      return document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  } catch {
    return false;
  }
}

const copyResetTimers = new WeakMap<
  HTMLButtonElement,
  ReturnType<typeof setTimeout>
>();

function showCopied(
  button: HTMLButtonElement,
  labels: TalerQrCodeLabels,
): void {
  const previousTimer = copyResetTimers.get(button);
  if (previousTimer !== undefined) clearTimeout(previousTimer);
  button.textContent = labels.copied;
  button.classList.add(
    "bg-emerald-100",
    "border-emerald-300",
    "text-emerald-900",
  );
  const timer = setTimeout(() => {
    button.textContent = labels.copy;
    button.classList.remove(
      "bg-emerald-100",
      "border-emerald-300",
      "text-emerald-900",
    );
    copyResetTimers.delete(button);
  }, 2000);
  copyResetTimers.set(button, timer);
}

function TalerLogo(): VNode {
  return (
    <svg
      class="taler-qr-logo"
      x="41.35"
      y="46.375"
      width="17.3"
      height="7.25"
      viewBox="0 0 196.2 87.3"
      aria-hidden="true"
    >
      <g
        fill="#0042b3"
        fill-rule="evenodd"
        stroke-width="0.3"
        transform="translate(-1,-1.1)"
      >
        <path d="m 86.7,1.1 c 15.6,0 29,9.4 36,23.2 h -5.9 A 35.1,35.1 0 0 0 86.7,6.5 C 67,6.5 51,23.6 51,44.7 c 0,10.4 3.8,19.7 10,26.6 a 31.4,31.4 0 0 1 -4.2,3 A 45.2,45.2 0 0 1 46,44.7 C 46,20.7 64.2,1.1 86.7,1.1 Z m 35.8,64.3 a 40.4,40.4 0 0 1 -39,22.8 c 3,-1.5 6,-3.5 8.6,-5.7 a 35.6,35.6 0 0 0 24.6,-17.1 z" />
        <path d="m 64.2,1.1 3.1,0.1 C 64.3,2.8 61.4,4.7 58.8,7 A 37.5,37.5 0 0 0 28.6,44.7 c 0,14.3 7.3,26.7 18,33.3 a 29.6,29.6 0 0 1 -8.5,0.2 c -9,-8 -14.6,-20 -14.6,-33.5 0,-24 18.2,-43.6 40.7,-43.6 z m 5.4,81.4 A 35.6,35.6 0 0 0 94.2,65.4 h 5.9 a 40.4,40.4 0 0 1 -39,22.8 c 3,-1.5 5.9,-3.5 8.5,-5.7 z M 94.4,24.3 A 37,37 0 0 0 81.8,11.5 29.6,29.6 0 0 1 90.3,11.3 c 4,3.6 7.4,8 9.9,13 z" />
        <path d="m 41.8,1.1 c 1,0 2,0 3.1,0.2 -3,1.5 -5.9,3.4 -8.5,5.6 A 37.5,37.5 0 0 0 6.1,44.7 c 0,21.1 16,38.3 35.7,38.3 12.6,0 23.6,-7 30,-17.6 h 5.8 a 40.4,40.4 0 0 1 -35.8,23 C 19.3,88.4 1,68.8 1,44.7 1,20.7 19.2,1.1 41.7,1.1 Z m 30.1,23.2 a 38.1,38.1 0 0 0 -4.5,-6.1 c 1.3,-1.2 2.7,-2.2 4.3,-3 2.3,2.7 4.4,5.8 6,9.1 z" />
      </g>
      <path
        fill="#0042b3"
        d="m 75.1,33.3 h 9.2 v -5 H 60.9 v 5 H 70 v 26 h 5.1 z m 16.5,18.5 h 13.7 l 3,7.4 h 5.3 L 100.9,28 H 96.2 L 83.5,59.2 h 5.2 z m 11.8,-4.9 h -9.9 l 5,-12.4 z m 19.4,-18.6 h -4.6 v 31 h 20.6 v -5 h -16 z m 42.7,0 H 144 v 31 h 21.6 v -5 H 149 V 46 h 14.5 V 41.1 H 149 v -8 h 16.4 z m 24.7,10.1 c 0,1.6 -0.5,2.8 -1.6,3.8 -1.1,1 -2.6,1.4 -4.4,1.4 h -7.4 V 33.2 h 7.4 c 1.9,0 3.4,0.4 4.4,1.3 1,0.9 1.6,2.2 1.6,3.9 z m 6,20.8 -7.7,-11.7 c 1,-0.3 1.9,-0.7 2.7,-1.3 a 8.8,8.8 0 0 0 3.6,-4.6 c 0.4,-1 0.5,-2.2 0.5,-3.5 0,-1.5 -0.2,-2.9 -0.7,-4.1 a 8.4,8.4 0 0 0 -2.1,-3.1 c -1,-0.8 -2,-1.5 -3.4,-2 -1.3,-0.4 -2.8,-0.6 -4.5,-0.6 h -12.9 v 31 h 5 v -11 h 6.5 l 7,10.8 z"
      />
    </svg>
  );
}

function TalerFrame({
  qrUrl,
  size,
  alt,
}: {
  qrUrl: string;
  size: number;
  alt: string;
}): VNode {
  const animate =
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function" ||
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return (
    <svg
      data-taler-qr-code
      class="taler-qr-frame h-auto max-w-full"
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={alt}
    >
      <title>{alt}</title>
      <rect width="100" height="100" rx="8" fill="#f1f1f4" />
      {TALER_QR_LINE_COLORS.map((color, index) => {
        const segmentLength = 57 / TALER_QR_LINE_COLORS.length;
        const start = -index * segmentLength;
        return (
          <rect
            key={index}
            class="taler-qr-motion-segment"
            x="4.9"
            y="4.9"
            width="90.2"
            height="90.2"
            rx="4.2"
            pathLength="400"
            fill="none"
            stroke={color}
            stroke-width="2.6"
            stroke-dasharray={`${segmentLength} ${200 - segmentLength}`}
            stroke-dashoffset={start}
          >
            {animate ? (
              <animate
                attributeName="stroke-dashoffset"
                from={String(start)}
                to={String(start - 400)}
                dur="8s"
                repeatCount="indefinite"
              />
            ) : undefined}
          </rect>
        );
      })}
      <rect
        x="6.25"
        y="6.25"
        width="87.5"
        height="87.5"
        rx="3.5"
        fill="#ffffff"
      />
      <image
        href={qrUrl}
        x="6.25"
        y="6.25"
        width="87.5"
        height="87.5"
        preserveAspectRatio="xMidYMid meet"
      />
      <rect
        x="36.5"
        y="43.75"
        width="27"
        height="12.5"
        rx="6.25"
        fill="#ffffff"
      />
      <TalerLogo />
    </svg>
  );
}

function StandardFrame({
  qrUrl,
  size,
  alt,
  swiss,
}: {
  qrUrl: string;
  size: number;
  alt: string;
  swiss: boolean;
}): VNode {
  return (
    <svg
      data-taler-qr-code
      class="h-auto max-w-full"
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={alt}
    >
      <title>{alt}</title>
      <rect width="100" height="100" rx="8" fill="#f3f4f6" stroke="#d1d5db" />
      <rect x="3" y="3" width="94" height="94" rx="5" fill="#ffffff" />
      <image
        href={qrUrl}
        x="5"
        y="5"
        width="90"
        height="90"
        preserveAspectRatio="xMidYMid meet"
      />
      {swiss ? (
        <svg
          class="swiss-qr-cross"
          x="42.3915"
          y="42.3915"
          width="15.217"
          height="15.217"
          viewBox="0 0 19.8 19.8"
          aria-hidden="true"
        >
          <polygon points="18.3,0.7 1.6,0.7 0.7,0.7 0.7,1.6 0.7,18.3 0.7,19.1 1.6,19.1 18.3,19.1 19.1,19.1 19.1,18.3 19.1,1.6 19.1,0.7" />
          <rect x="8.3" y="4" width="3.3" height="11" fill="#ffffff" />
          <rect x="4.4" y="7.9" width="11" height="3.3" fill="#ffffff" />
          <polygon
            points="0.7,1.6 0.7,18.3 0.7,19.1 1.6,19.1 18.3,19.1 19.1,19.1 19.1,18.3 19.1,1.6 19.1,0.7 18.3,0.7 1.6,0.7 0.7,0.7"
            fill="none"
            stroke="#ffffff"
            stroke-width="1.4357"
            stroke-miterlimit="10"
          />
        </svg>
      ) : undefined}
    </svg>
  );
}

export function TalerQrCode({
  url,
  copyUrl,
  size = 280,
  alt = "Payment QR code",
  variant = "taler",
  labels: labelOverrides,
}: TalerQrCodeProps): VNode {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const qrResult = generateQrDataUrl(url);
  const textToCopy = copyUrl || url;

  const handleCopy = async (button: HTMLButtonElement): Promise<void> => {
    if (!(await copyText(textToCopy))) return;
    showCopied(button, labels);
  };

  return (
    <div class="inline-flex max-w-full flex-col items-center gap-4">
      {qrResult.type === "success" ? (
        variant === "taler" ? (
          <TalerFrame qrUrl={qrResult.url} size={size} alt={alt} />
        ) : (
          <StandardFrame
            qrUrl={qrResult.url}
            size={size}
            alt={alt}
            swiss={variant === "swiss"}
          />
        )
      ) : (
        <svg
          data-taler-qr-code
          class="h-auto max-w-full"
          width={size}
          height={size}
          viewBox="0 0 100 100"
          role="img"
          aria-label={labels.generationFailed}
        >
          <rect
            width="100"
            height="100"
            rx="8"
            fill="#f9fafb"
            stroke="#d1d5db"
          />
          <text
            x="50"
            y="51"
            text-anchor="middle"
            fill="#b91c1c"
            font-size="3"
            font-weight="600"
          >
            {labels.generationFailed}
          </text>
        </svg>
      )}

      {copyUrl || qrResult.type === "error" ? (
        <button
          type="button"
          class="taler-qr-copy-button cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          aria-live="polite"
          onClick={(event) => void handleCopy(event.currentTarget)}
        >
          {labels.copy}
        </button>
      ) : undefined}
    </div>
  );
}
